import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'

import { borderClassesError } from '../form/variants'

export const inputBaseClasses = [
  'block',
  'w-full',
  'rounded-md',
  'border',
  'border-border',
  'bg-white',
  'px-3.5',
  'py-[7px]',
  'text-sm',
  'placeholder:text-neutral-400',
  'dark:placeholder:text-neutral-400',
  'disabled:bg-neutral-100',
  'dark:border-neutral-800',
  'dark:bg-neutral-950',
  'dark:focus-visible:ring-neutral-300',
  'file:border-0',
  'file:bg-transparent',
  'file:text-sm',
  'file:font-medium',
  'focus-visible:outline-none',
  'focus-visible:ring-1',
  'focus-visible:ring-neutral-800',
  'focus-visible:ring-inset',
  'focus-visible:border-neutral-800',
  'disabled:cursor-not-allowed',
  'disabled:opacity-50',
]

export const inputVariantListBase = {
  size: {
    '2xs': 'h-6 px-2 text-xs',
    xs: 'h-[28px] px-3 text-xs',
    sm: 'h-8 px-[14px] text-xs',
    md: 'h-9 px-4 text-sm',
    lg: 'h-11 px-[18px] text-base',
  },
}

export const inputVariantList = {
  size: {
    '2xs': [
      inputVariantListBase.size['2xs'],
      'group-has-[[data-icon=leading]]:pl-6 group-has-[[data-icon=trailing]]:pr-6',
    ],
    xs: [
      inputVariantListBase.size.xs,
      'group-has-[[data-icon=leading]]:pl-[30px] group-has-[[data-icon=trailing]]:pr-[30px]',
    ],
    sm: [
      inputVariantListBase.size.sm,
      'group-has-[[data-icon=leading]]:pl-[34px] group-has-[[data-icon=trailing]]:pr-[34px]',
    ],
    md: [
      inputVariantListBase.size.md,
      'group-has-[[data-icon=leading]]:pl-[42px] group-has-[[data-icon=trailing]]:pr-[42px]',
    ],
    lg: [
      inputVariantListBase.size.lg,
      'group-has-[[data-icon=leading]]:pl-11 group-has-[[data-icon=trailing]]:pr-11',
    ],
  },
  hasError: {
    true: borderClassesError,
  },
}

export const inputVariants = cva(
  [
    ...inputBaseClasses,
    'file:text-sm',
    'file:font-medium',
  ],
  {
    variants: inputVariantList,
  },
)

export const inputFieldClasses = [
  'relative',
]

export const inputIconVariants = cva([
  'absolute',
  'top-1/2',
  '-translate-y-1/2',
  'pointer-events-none',
  'dark:border-neutral-800',
  'dark:bg-neutral-950',
  'dark:placeholder:text-neutral-400',
  'dark:focus-visible:ring-neutral-300',
], {
  variants: {
    size: {
      '2xs': 'size-2.5',
      xs: 'size-3',
      sm: 'size-3.5',
      md: 'size-4',
      lg: 'size-[16px]',
    },
    position: {
      leading: 'left-3',
      trailing: 'right-3',
    },
  },
  compoundVariants: [
    {
      position: 'leading',
      size: '2xs',
      class: 'left-2',
    },
    {
      position: 'leading',
      size: 'xs',
      class: 'left-3',
    },
    {
      position: 'leading',
      size: 'sm',
      class: 'left-[14px]',
    },
    {
      position: 'leading',
      size: 'md',
      class: 'left-4',
    },
    {
      position: 'leading',
      size: 'lg',
      class: 'left-[18px]',
    },
    {
      position: 'trailing',
      size: '2xs',
      class: 'right-2',
    },
    {
      position: 'trailing',
      size: 'xs',
      class: 'right-3',
    },
    {
      position: 'trailing',
      size: 'sm',
      class: 'right-[14px]',
    },
    {
      position: 'trailing',
      size: 'md',
      class: 'right-4',
    },
    {
      position: 'trailing',
      size: 'lg',
      class: 'right-[18px]',
    },
  ],
})

export type InputVariants = VariantProps<typeof inputVariants>
export type InputIconVariants = VariantProps<typeof inputIconVariants>
